import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { periodApi } from "../../../api/periods";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText } from "../../../components/ui";

type Kind = "review" | "finalize" | "reopen";
const CATS: [string, string][] = [["CORRECTION_NEEDED", "Correction needed"], ["CALCULATION_ERROR", "Calculation error"], ["POLICY_CHANGE", "Policy change"], ["OTHER", "Other"]];
const TITLE: Record<Kind, string> = { review: "Mark period in review", finalize: "Finalize & lock period", reopen: "Reopen finalized period" };
const sel = "h-10 w-full rounded-sm border border-white/15 bg-ink px-3 text-sm text-white";

export function PeriodActions({ periodId, status, blocking, hasApprovedRun, onDone }: { periodId: string; status: string; blocking: number | null; hasApprovedRun: boolean; onDone: () => void }) {
  const [ack, setAck] = useState(false);
  const [kind, setKind] = useState<Kind | null>(null);
  const [note, setNote] = useState("");
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const calc = async () => {
    setBusy(true);
    try { await periodApi.calculate(periodId); toast.success("Draft snapshot calculated"); onDone(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const go = async () => {
    setBusy(true); setErr(null);
    try {
      if (kind === "review") await periodApi.review(periodId, note);
      if (kind === "finalize") await periodApi.finalize(periodId, note);
      if (kind === "reopen") await periodApi.reopen(periodId, cat, note, ack);
      toast.success(`${TITLE[kind!]} — done`);
      setKind(null); setNote(""); setCat(""); onDone();
    } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
  };
  const locked = status === "FINALIZED";
  return (
    <div className="flex flex-wrap gap-2" data-testid="period-actions">
      {!locked && <Button variant="secondary" onClick={calc} loading={busy && !kind} data-testid="period-calculate-btn">Calculate snapshot</Button>}
      {(status === "OPEN" || status === "REOPENED") && <Button variant="secondary" onClick={() => setKind("review")} data-testid="period-review-btn">Mark in review</Button>}
      {!locked && <Button onClick={() => setKind("finalize")} disabled={blocking !== 0} title={blocking ? "Resolve every blocking item first" : undefined} data-testid="period-finalize-btn">Finalize</Button>}
      {locked && <Button variant="secondary" onClick={() => setKind("reopen")} data-testid="period-reopen-btn">Reopen</Button>}
      <Modal open={!!kind} title={kind ? TITLE[kind] : ""} onClose={() => setKind(null)} testId="period-action-modal">
        <div className="space-y-3 text-sm">
          {kind === "finalize" && <p className="text-zinc-300">A fresh FINAL snapshot is stored and the period is locked: payable edits, breaks, corrections, exception resolutions, approvals and rate corrections for its sessions are refused until reopened.</p>}
          {kind === "reopen" && (
            <>
              <p className="text-amber-200" data-testid="period-reopen-warning">The current FINAL snapshot is kept forever (marked superseded). Edits become possible again; re-finalizing creates a new version.</p>
              {hasApprovedRun && <label className="flex items-start gap-2 rounded-sm border border-red-500/40 bg-red-500/10 p-3 text-red-100" data-testid="period-reopen-payroll-warning"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="period-reopen-payroll-ack" />This period has an APPROVED payroll run. Reopening marks it NEEDS_NEW_VERSION; a new snapshot will need a new payroll run version.</label>}
              <select className={sel} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="period-reopen-category"><option value="">Reason category…</option>{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </>
          )}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} placeholder="Note (required)" className="w-full rounded-sm border border-white/15 bg-ink p-2 text-white" data-testid="period-action-note" />
          <ErrorText message={err} testId="period-action-error" />
          <Button onClick={go} loading={busy} disabled={!note.trim() || (kind === "reopen" && (!cat || (hasApprovedRun && !ack)))} data-testid="period-action-confirm">Confirm</Button>
        </div>
      </Modal>
    </div>
  );
}
